import {useCallback, useEffect, useState} from "react"
import {ChevronLeft, ChevronRight, Download, Info, Maximize2, Minimize2, X} from "lucide-react"

const ImageGalleryOverlay = ({images, initialIndex = 0, onClose}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [showInfo, setShowInfo] = useState(false);

    const currentImage = images[currentIndex];

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
                case ' ':
                    e.preventDefault();
                    toggleFullscreen();
                    break;
                case 'i':
                case 'I':
                    setShowInfo(!showInfo);
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, showInfo]);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
        setIsLoading(true);
    }, [images.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
        setIsLoading(true);
    }, [images.length]);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(console.log);
        } else {
            document.exitFullscreen();
        }
    }, []);

    const handleDownload = useCallback(() => {
        const link = document.createElement('a');
        link.href = currentImage.src;
        link.download = currentImage.title || `image-${currentIndex + 1}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [currentImage, currentIndex]);

    // Touch handlers
    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e) => {
        if (!touchStart) return;
        const touchEnd = e.changedTouches[0].clientX;
        const distance = touchStart - touchEnd;
        if (Math.abs(distance) > 50) {
            distance > 0 ? goToNext() : goToPrevious();
        }
        setTouchStart(null);
    };

    return (
        <div className="fixed inset-0 " style={{backgroundColor:"rgba(0, 0, 0, 0.2)"}}>

            <button
                onClick={onClose}
                style={{top:80, cursor:"pointer", backgroundColor:"rgba(0, 0, 0, 0.2)"}}
                className="absolute top-4 right-4 z-50 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors backdrop-blur-sm"
                aria-label="Close gallery"
            >
                <X size={24}/>
            </button>

            {/* Main Image Area - Full screen with arrows on sides */}
            <div
                className="h-full w-full flex items-center justify-center"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {/* Left Arrow */}
                <button
                    onClick={goToPrevious}
                    style={{ cursor:"pointer", left:20, backgroundColor:"rgba(0, 0, 0, 0.2)" }}
                    className="absolute top-1/2 -translate-y-1/2 z-40 p-4 rounded-full bg-black/40 hover:bg-black/60 text-white transition-all"
                    aria-label="Previous image"
                >
                    <ChevronLeft size={28}/>
                </button>

                {/* Right Arrow */}
                <button
                    onClick={goToNext}
                    style={{ cursor:"pointer", right:20, backgroundColor:"rgba(0, 0, 0, 0.2)" }}
                    className="absolute top-1/2 -translate-y-1/2 z-40 p-4 rounded-full bg-black/40 hover:bg-black/60 text-white transition-all"
                    aria-label="Next image"
                >
                    <ChevronRight size={28}/>
                </button>

                {/* Main Image */}
                <div className=" w-full h-full flex items-center justify-center p-4">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-30">
                            <div
                                className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"/>
                        </div>
                    )}

                    <img
                        src={currentImage.src}
                        style={{
                            height:"40rem",
                            width:"auto",
                            objectFit:"cover"
                        }}
                        alt={currentImage.alt || currentImage.title || `Gallery image ${currentIndex + 1}`}
                        onLoad={() => setIsLoading(false)}
                        draggable="false"
                    />
                </div>
            </div>

            {/* ALL CONTROLS AT THE BOTTOM - Fixed position */}
            <div
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent pt-8 pb-4 px-4">
                <div className="max-w-6xl mx-auto">
                    {/* Image Title */}
                    <div className="text-center mb-4">
                        <h3 className="text-white text-xl font-semibold truncate">
                            {currentImage.title}
                        </h3>
                        {currentImage.description && !showInfo && (
                            <p className="text-white/70 text-sm mt-1 truncate">
                                {currentImage.description}
                            </p>
                        )}
                    </div>

                    {/* Thumbnail Strip - Centered */}
                    <div className="mb-6 overflow-x-auto">
                        <div className="flex gap-2 justify-center pb-2">
                            {images.map((img, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setCurrentIndex(index);
                                        setIsLoading(true);
                                    }}
                                    className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                                        index === currentIndex
                                            ? 'border-white scale-110 shadow-lg shadow-white/20'
                                            : 'border-transparent hover:border-white/40'
                                    }`}
                                    aria-label={`Go to image ${index + 1}`}
                                >
                                    <img
                                        src={img.thumbnail || img.src}
                                        alt=""
                                        className="w-full h-full object-cover"
                                        draggable="false"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Control Buttons - Centered in a row */}
                    <div className="flex items-center justify-center gap-4">
                        {/* Image Counter */}
                        <div className="text-white font-medium px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
                            {currentIndex + 1} / {images.length}
                        </div>

                        {/* Divider */}
                        <div className="h-6 w-px bg-white/20"/>

                        {/* Download Button */}
                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur-sm"
                            aria-label="Download image"
                        >
                            <Download size={18}/>
                            <span className="font-medium">Download</span>
                        </button>

                        {/* Info Toggle */}
                        <button
                            onClick={() => setShowInfo(!showInfo)}
                            className={`p-2.5 rounded-lg transition-colors ${
                                showInfo
                                    ? 'bg-white text-black'
                                    : 'bg-white/10 hover:bg-white/20 text-white'
                            }`}
                            aria-label="Toggle info"
                        >
                            <Info size={20}/>
                        </button>

                        {/* Fullscreen Toggle */}
                        <button
                            onClick={toggleFullscreen}
                            className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        >
                            {isFullscreen ? <Minimize2 size={20}/> : <Maximize2 size={20}/>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Grid Component
const ImageGalleryGrid = ({images = [], onImageClick}) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 md:p-6">
            {images.map((image, index) => (
                <div
                    key={index}
                    className="group relative aspect-square overflow-hidden rounded-xl bg-gray-800 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                    onClick={() => onImageClick(index)}
                >
                    <img
                        src={image.src}
                        alt={image.alt || image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                    />

                    {/* Gradient overlay */}
                    <div
                        className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>

                    {/* Title at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-white font-medium truncate text-sm">{image.title}</h3>
                    </div>

                    {/* View indicator */}
                    <div
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                    </div>
                </div>
            ))}
        </div>
    )
}


export const useImageGallery = () => {
    const [galleryState, setGalleryState] = useState({
        isOpen: false,
        images: [],
        initialIndex: 0,
    })

    const openGallery = (images, initialIndex = 0) => {
        setGalleryState({
            isOpen: true,
            images,
            initialIndex,
        })
    }

    const closeGallery = () => {
        setGalleryState(prev => ({...prev, isOpen: false}))
    }

    const GalleryComponent = () => {
        if (!galleryState.isOpen) return null

        return (
            <ImageGalleryOverlay
                images={galleryState.images}
                initialIndex={galleryState.initialIndex}
                onClose={closeGallery}
            />
        )
    }

    return {GalleryComponent, openGallery, closeGallery}
}


const ImageGallery = ({images = []}) => {
    const {GalleryComponent, openGallery} = useImageGallery()

    return (
        <>
            <div>
                {images.map((image, index) => (
                    <div
                        key={index}
                        className="relative group cursor-pointer overflow-hidden rounded-xl shadow-lg"
                        onClick={() => openGallery(images, index)}
                    >
                        {/* Image */}
                        <div className="aspect-square overflow-hidden">
                            <img
                                src={image.src}
                                alt={image.alt || image.title}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                loading="lazy"
                            />
                        </div>

                        {/* Overlay on hover */}
                        <div
                            className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                            <div
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center p-4">
                                <h3 className="font-semibold text-lg mb-2">{image.title}</h3>
                                {image.description && (
                                    <p className="text-sm text-white/80 line-clamp-2">
                                        {image.description}
                                    </p>
                                )}
                                <div
                                    className="mt-4 inline-flex items-center gap-2 text-sm bg-white/20 px-3 py-1 rounded-full">
                                    <span>Click to view</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick info */}
                        <div
                            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-white">
                            <h4 className="font-medium truncate">{image.title}</h4>
                            <div className="flex items-center justify-between text-xs text-white/60 mt-1">
                                {image.date && <span>{image.date}</span>}
                                {image.size && <span>{image.size}</span>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Render the gallery overlay */}
            <GalleryComponent/>
        </>
    )
}

export default ImageGallery
export {ImageGalleryOverlay}
